(function () {
  const fallbackData = {
    1: {
      name: "Age",
      required: true,
      multiple: false,
      value: "",
      filter: "quiz.age",
    },
    2: {
      name: "Weight",
      required: false,
      multiple: false,
      value: "",
      filter: "quiz.weight",
    },
    3: {
      name: "Problems",
      required: false,
      multiple: true,
      value: [],
      filter: "quiz.problems",
    },
    4: {
      name: "Intolerances",
      required: false,
      multiple: true,
      value: [],
      filter: "quiz.intolerances",
      allValues: [
        "Keine",
        "Rind",
        "Getreide",
        "Lamm",
        "Kartoffeln",
        "Geflügel",
        "Wild",
        "Fisch",
      ],
    },
  };
  class Quiz extends HTMLElement {
    #currentStep = 0;
    #previousStep = null;

    constructor() {
      super();
      this.startButtons = [...this.querySelectorAll(".js-start")];
      this.finishButton = this.querySelector(".js-finish");

      this.mainActions = this.querySelector(".js-main-buttons");
      this.backButton = this.mainActions.querySelector(".js-back");
      this.nextButton = this.mainActions.querySelector(".js-next");

      this.progressBar = this.querySelector(".js-progress");
      this.progressItems = [
        ...this.progressBar.querySelectorAll(".js-progress-item"),
      ];

      this.steps = this.querySelectorAll(".js-quiz-block");

      this.ageInput = this.steps[1]?.querySelector(".js-action");
      this.weightInput = this.steps[2]?.querySelector(".js-action");
      this.problemsInput = this.steps[3]?.querySelector(".js-action");
      this.intolerancesInput = this.steps[4]?.querySelector(".js-action");

      this.data = null;
      this.collectionsAllUrl = this.dataset?.url;
    }

    get currentStep() {
      return this.#currentStep;
    }

    set currentStep(value) {
      const parsedValue = Number(value);
      if (!isNaN(parsedValue) && parsedValue !== this.#currentStep) {
        this.#previousStep = this.#currentStep;
        this.#currentStep = parsedValue;
        this.onStepChanged(this.#previousStep, this.#currentStep);
      }
    }

    toggleActions(prev, current) {
      const toggle = (elements) =>
        elements.forEach((el) => el.classList.toggle("hidden"));

      if ((prev === 0 && current === 1) || (prev === 1 && current === 0)) {
        toggle([this.progressBar, ...this.startButtons, this.mainActions]);
      }
      if (prev === this.steps.length - 2 && current === this.steps.length - 1) {
        toggle([this.mainActions, this.finishButton]);
      }
    }

    updateProgressBar(prev, current) {
      const index = prev < current ? current : prev;
      this.progressItems
        .find((pi) => Number(pi.dataset?.index) === index)
        ?.classList.toggle("active");
    }

    validateStep(prev, current) {
      if (current < prev) return;

      if (this.data[current]?.required && !this.data[current]?.value) {
        this.nextButton.setAttribute("disabled", "");
      }
    }

    onStepChanged(prev, current) {
      this.updateProgressBar(prev, current);
      this.toggleActions(prev, current);
      this.showStep(current);
      this.validateStep(prev, current);
    }

    goBack() {
      if (this.currentStep === 0) return;
      this.currentStep -= 1;
    }

    goNext() {
      if (this.currentStep >= this.steps.length - 1) return;
      this.currentStep += 1;
    }

    showStep(index) {
      this.steps.forEach((step) => {
        Number(step.dataset?.index) === index
          ? step.classList.remove("hidden")
          : step.classList.add("hidden");
      });
    }

    updateInDesignMode(index) {
      this.showStep(index);
      const isFirstStep = index === 0;
      const isLastStep = index === this.steps.length - 1;
    
      this.mainActions.classList.toggle("hidden", isFirstStep || isLastStep);
    
      this.startButtons.forEach((el) => {
        el.classList.toggle("hidden", !isFirstStep);
      });
    
      this.finishButton.classList.toggle("hidden", !isLastStep);
    
      if (isFirstStep) {
        this.progressBar.classList.add("hidden");
      } else {
        this.progressBar.classList.remove("hidden");
        this.progressItems.forEach((el) => {
          const itemIndex = Number(el.dataset?.index);
          el.classList.toggle("active", itemIndex <= index);
        });
      }
    }

    listenDesignMode() {
      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) => {
          const index = Number(e.target?.dataset?.index);
          this.updateInDesignMode(index);
        });
      }
    }

    updateTextInput(e, questionIndex) {
      const value = e.target.value;

      if (!value) return;
      this.data[questionIndex].value = value;
      this.nextButton.removeAttribute("disabled");
    }

    updateCustomSelect(questionIndex) {
      const selectValue = this.weightInput.getAttribute("value");
      if (!selectValue || this.data[questionIndex].value === selectValue)
        return;
      this.data[questionIndex].value = selectValue;
    }

    updateCheckboxInput(e, questionIndex) {
      if (e.target.type !== "checkbox") return;

      const value = e.target.value;
      const isChecked = e.target.checked;
      isChecked
        ? this.data[questionIndex].value.push(value)
        : (this.data[questionIndex].value = this.data[
            questionIndex
          ].value.filter((item) => item !== value));
    }

    updateData(e) {
      const questionIndex = Number(e.currentTarget?.dataset?.index);
      if (!questionIndex) return;

      switch (questionIndex) {
        case 1:
          this.updateTextInput(e, questionIndex);
          break;
        case 2:
          this.updateCustomSelect(questionIndex);
          break;
        default:
          this.updateCheckboxInput(e, questionIndex);
      }
    }

    buildQueryParams(data) {
      const prefix = "filter.p.m.";
      const searchParams = new URLSearchParams();

      function getInverseValues(allValues, selectedValues) {
        return allValues.filter((val) => !selectedValues.includes(val));
      }

      function addParams(filter, values) {
        values.forEach((val) => {
          searchParams.append(`${prefix}${filter}`, val);
        });
      }
      Object.values(data).forEach((item) => {
        if (
          item.multiple &&
          Array.isArray(item.value) &&
          item.value.length > 0
        ) {
          if (item.filter === "quiz.intolerances" && item.allValues) {
            const inverseValues = getInverseValues(item.allValues, item.value);
            addParams(item.filter, inverseValues);
          } else {
            addParams(item.filter, item.value);
          }
        } else if (!item.multiple && item.value?.trim() !== "") {
          addParams(item.filter, [item.value]);
        }
      });
      searchParams.append("view", "quiz-results");
      return `&${searchParams.toString()}`;
    }

    appendQueryParamsToUrl(queryParams) {
      const url = new URL(this.collectionsAllUrl, window.location.origin);
      const sanitizedQueryParams = queryParams.startsWith("&")
        ? queryParams.slice(1)
        : queryParams;
      url.search += sanitizedQueryParams;
      return url.toString();
    }

    findResults() {
      const queryParams = this.buildQueryParams(this.data);
      const fullUrl = this.appendQueryParamsToUrl(queryParams);
      window.location.href = fullUrl;
    }

    setData() {
      const data = document.getElementById("quiz_data");
      if (!data) {
        this.data = fallbackData;
        return;
      }
      try {
        const parsedData = JSON.parse(data.innerHTML);
        Object.keys(parsedData).length === 0
          ? (this.data = fallbackData)
          : (this.data = parsedData);
      } catch (error) {
        this.data = fallbackData;
      }
    }

    connectedCallback() {
      this.backButton.addEventListener("click", this.goBack.bind(this));
      this.nextButton.addEventListener("click", this.goNext.bind(this));
      this.startButtons.forEach((button) => {
        button.addEventListener("click", this.goNext.bind(this));
      });
      this.ageInput.addEventListener("click", this.updateData.bind(this));
      this.weightInput.addEventListener("click", this.updateData.bind(this));
      this.problemsInput.addEventListener("change", this.updateData.bind(this));
      this.intolerancesInput.addEventListener(
        "change",
        this.updateData.bind(this)
      );
      this.finishButton.addEventListener("click", this.findResults.bind(this));
      this.setData();
      this.listenDesignMode();
    }
  }
  if (!customElements.get("quiz-frame")) {
    customElements.define("quiz-frame", Quiz);
  }
})();
